/**
 * Dependencies.
 */
import { orderBy, uniqBy } from 'lodash-es';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import VueSignaturePad from 'vue-signature-pad';

import { servicePointOrderService } from '@/api/service-point/order/order.service';
import OrderSteps from '@/modules/service-point/components/order/order-steps/order-steps.component.vue';
import { servicePointStore } from '@/modules/service-point/store';

/**
 * Component.
 */
@Component({
  components: {
    'order-steps': OrderSteps,
    'signature-pad': VueSignaturePad
  },
  name: 'collect-order-confirm-scene'
})
export default class extends Vue {
  /**
   * Property `isCollecting`.
   */
  isCollecting = false;

  /**
   * Getter `clientName`.
   */
  get clientName() {
    return this.order?.delivery?.client?.name;
  }

  /**
   * Getter `order`.
   */
  get order() {
    const { orders = [] } = this;
    const { order } = servicePointStore;

    const orderInList = orders?.find(({ id }) => id === order?.id);

    const orderCode =
      order?.delivery?.orderCode || orderInList?.delivery?.orderCode;

    return {
      ...order,
      delivery: {
        ...order?.delivery,
        orderCode: orderCode || null
      }
    };
  }

  /**
   * Getter `orders`.
   */
  get orders() {
    return servicePointStore.orders;
  }

  /**
   * Getter `orderCode`.
   */
  get orderCode() {
    return this.order?.delivery?.orderCode;
  }

  /**
   * Getter `packages`.
   */
  get packages() {
    let { packages = [] } = this.order;

    packages = uniqBy(packages, 'id');
    packages = orderBy(packages, ['status.code'], ['desc']);

    return packages;
  }

  /**
   * Getter `trackingNumber`.
   */
  get trackingNumber() {
    return this.order?.delivery?.trackingNumber;
  }

  /**
   * Getter `trackingNumberKey`.
   */
  get trackingNumberKey() {
    const trackingNumberKey = servicePointStore.deliverTrackingNumberKey;

    return trackingNumberKey;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Getter `totalSteps`.
   */
  get totalSteps() {
    const { totalSteps = null } = this.$route.params || {};

    return Number(totalSteps) || 3;
  }

  /**
   * Lifecycle created.
   */
  async created() {
    const { trackingNumberKey } = this;

    if (!trackingNumberKey) {
      this.$router.push({
        name: 'ServicePointHome'
      });
    }
  }

  /**
   * Handle confirm collect.
   */
  async handleConfirmCollect() {
    try {
      this.isCollecting = true;

      await servicePointOrderService.markHasCollected({
        trackingNumber: this.trackingNumber
      });

      this.$router.push({
        name: 'ServicePointCollectOrderStatus',
        params: {
          totalSteps: String(this.totalSteps)
        }
      });
    } catch (error) {
      this.$kit.notify.error(
        this.i18n('SERVICE_POINT.LABELS.ERROR'),
        this.i18n('SERVICE_POINT.ERRORS.SOMETHING_WENT_WRONG')
      );

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleConfirmCollect' });
      }
    } finally {
      this.isCollecting = false;
    }
  }

  /**
   * Handle go back.
   */
  handleGoback() {
    this.$router.back();
  }
}
